(() => {
  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

  const init = (root) => {
    if (root.dataset.kalluceV2Initialized === 'true') return;
    root.dataset.kalluceV2Initialized = 'true';

    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const dots = [...root.querySelectorAll('[data-gallery-dot]')];
    let galleryIndex = 0;

    const showMedia = (requestedIndex) => {
      if (!slides.length) return;
      galleryIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === galleryIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      thumbs.forEach((thumb, index) => {
        const active = index === galleryIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });
      dots.forEach((dot, index) => {
        if (index === galleryIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      thumbs[galleryIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(Number(thumb.dataset.galleryIndex || 0))));
    dots.forEach((dot) => dot.addEventListener('click', () => showMedia(Number(dot.dataset.galleryIndex || 0))));
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => showMedia(galleryIndex - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => showMedia(galleryIndex + 1));

    let touchStartX = 0;
    root.querySelector('.kalluce-v2__stage')?.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0]?.clientX || 0;
    }, { passive: true });
    root.querySelector('.kalluce-v2__stage')?.addEventListener('touchend', (event) => {
      const touchEndX = event.changedTouches[0]?.clientX || 0;
      if (Math.abs(touchEndX - touchStartX) < 42) return;
      showMedia(galleryIndex + (touchEndX < touchStartX ? 1 : -1));
    }, { passive: true });

    const modal = root.querySelector('[data-media-modal]');
    const modalImage = modal?.querySelector('img');
    const openModal = (sourceImage) => {
      if (!modal || !modalImage || !sourceImage) return;
      modalImage.src = sourceImage.currentSrc || sourceImage.src;
      modalImage.alt = sourceImage.alt;
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    };
    root.querySelectorAll('[data-gallery-zoom]').forEach((button) => button.addEventListener('click', () => openModal(button.querySelector('img'))));
    root.querySelectorAll('[data-review-media-open]').forEach((button) => button.addEventListener('click', () => openModal(button.querySelector('img'))));
    modal?.querySelector('[data-modal-close]')?.addEventListener('click', () => modal.close?.());
    modal?.addEventListener('click', (event) => {
      if (event.target === modal) modal.close?.();
    });

    const packCards = [...root.querySelectorAll('[data-pack-card]')];
    const moonHost = root.querySelector('[data-moon-host]');
    const bundleStatus = root.querySelector('[data-bundle-status]');
    const mainSubmit = root.querySelector('[data-main-submit]');
    const productForm = mainSubmit?.closest('form');
    const sticky = root.querySelector('[data-sticky-atc]');
    const stickyColor = root.querySelector('[data-sticky-color]');
    const nativeAvailable = mainSubmit?.dataset.nativeAvailable === 'true';
    let moonComponent = null;
    let moonSyncFrame = 0;
    let internalMoonSync = false;
    const expectedColors = ['Bleu', 'Noir', 'Chair', 'Rose'].map(normalize);

    const selectedCard = () => packCards.find((card) => card.querySelector('[data-pack-radio]')?.checked) || packCards[1] || packCards[0];
    const moonOptions = () => moonComponent ? [...moonComponent.querySelectorAll('.moonbundle-option-wrapper')] : [];
    const activeMoonOption = () => moonComponent?.querySelector('.moonbundle-option-active')?.closest('.moonbundle-option-wrapper')
      || moonComponent?.querySelector('.moonbundle-option-active');

    const moonIsReadyFor = (card = selectedCard()) => {
      if (!moonComponent || !card) return false;
      const options = moonOptions();
      if (options.length !== 3) return false;
      const quantity = Number(card.dataset.quantity || 0);
      const wrapper = options[quantity - 1];
      if (!wrapper || activeMoonOption() !== wrapper) return false;
      const rows = [...wrapper.querySelectorAll('.moonbundle-variant-row')];
      const customSelects = [...card.querySelectorAll('[data-color-select]')];
      if (rows.length !== quantity || customSelects.length !== quantity) return false;
      return rows.every((row, index) => {
        const select = row.querySelector('select');
        if (!select) return false;
        const availableColors = [...select.options].flatMap((option) => [normalize(option.textContent), normalize(option.value)]);
        const wanted = normalize(customSelects[index].value);
        const wantedOption = [...select.options].find((option) => normalize(option.textContent) === wanted || normalize(option.value) === wanted);
        return expectedColors.every((color) => availableColors.includes(color))
          && wantedOption && select.value === wantedOption.value;
      });
    };

    const nativeSubmitBusy = () => mainSubmit?.classList.contains('loading')
      || mainSubmit?.getAttribute('aria-busy') === 'true';

    const setSubmitState = (ready) => {
      if (!mainSubmit) return;
      // Shrine owns the asynchronous request. Never clear its loading lock.
      const enabled = ready && nativeAvailable && !nativeSubmitBusy();
      root.classList.toggle('has-moon-v2', enabled);
      if (mainSubmit.disabled === enabled) mainSubmit.disabled = !enabled;
      if (mainSubmit.getAttribute('aria-disabled') !== String(!enabled)) {
        mainSubmit.setAttribute('aria-disabled', String(!enabled));
      }
      const label = mainSubmit.querySelector('span');
      if (!label) return;
      const text = nativeAvailable ? 'AJOUTER AU PANIER' : 'TEMPORAIREMENT ÉPUISÉ';
      if (label.textContent !== text) label.textContent = text;
    };

    const setBundleStatus = (text) => {
      if (bundleStatus && bundleStatus.textContent !== text) bundleStatus.textContent = text;
    };

    const refreshMoonState = () => {
      const ready = moonIsReadyFor(selectedCard());
      setSubmitState(ready);
      if (moonComponent) setBundleStatus(ready ? 'Offre Moon Bundle active.' : 'Vérification de l’offre Moon Bundle…');
    };

    const setMoonSelectValue = (moonSelect, wantedColor) => {
      const wanted = normalize(wantedColor);
      const option = [...moonSelect.options].find((item) => normalize(item.textContent) === wanted || normalize(item.value) === wanted);
      if (!option || moonSelect.value === option.value) return;
      moonSelect.value = option.value;
      moonSelect.dispatchEvent(new Event('input', { bubbles: true }));
      moonSelect.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const syncColorsToMoon = (card = selectedCard()) => {
      if (!moonComponent || !card || card !== selectedCard()) return;
      const active = activeMoonOption();
      if (active !== moonOptions()[Number(card.dataset.quantity || 0) - 1]) return;
      const customSelects = [...card.querySelectorAll('[data-color-select]')];
      customSelects.forEach((customSelect, index) => {
        // Moon may replace its rows synchronously after each variant change.
        const rows = [...(activeMoonOption()?.querySelectorAll('.moonbundle-variant-row') || [])];
        const moonSelect = rows[index]?.querySelector('select');
        if (moonSelect) setMoonSelectValue(moonSelect, customSelect.value);
      });
    };

    const setMoonPack = (card) => {
      if (!moonComponent || internalMoonSync || !card) return;
      // A harmless Moon rerender must not temporarily disable an already-ready CTA.
      setSubmitState(moonIsReadyFor(card));
      const targetIndex = Math.max(0, Number(card.dataset.quantity || 1) - 1);
      const wrapper = moonOptions()[targetIndex];
      const option = wrapper?.querySelector('.moonbundle-option') || wrapper;
      // Guard only synchronous event dispatch. The existing observer reconciles
      // Moon's next render without dropping newer pack or color selections.
      internalMoonSync = true;
      try {
        if (option && !option.classList.contains('moonbundle-option-active')) option.click();
        syncColorsToMoon(card);
      } finally {
        internalMoonSync = false;
      }
      refreshMoonState();
    };

    const syncStickyColor = (card = selectedCard()) => {
      if (!stickyColor || !card) return;
      const firstColor = card.querySelector('[data-color-select]')?.value;
      if (firstColor) stickyColor.value = firstColor;
    };

    const selectPack = (card, syncMoon = true) => {
      if (!card) return;
      packCards.forEach((item) => {
        const active = item === card;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-expanded', String(active));
        item.querySelector('.kalluce-v2__pack-body')?.setAttribute('aria-hidden', String(!active));
        const radio = item.querySelector('[data-pack-radio]');
        if (radio) radio.checked = active;
      });
      syncStickyColor(card);
      if (syncMoon) setMoonPack(card);
    };

    packCards.forEach((card) => {
      card.querySelector('[data-pack-radio]')?.addEventListener('change', () => selectPack(card));
      card.addEventListener('click', (event) => {
        if (event.target.closest('select')) return;
        if (event.target.matches('[data-pack-radio]')) return;
        selectPack(card);
      });
      const cardColorSelects = [...card.querySelectorAll('[data-color-select]')];
      cardColorSelects.forEach((select, index) => select.addEventListener('change', () => {
        selectPack(card, false);
        if (index === 0) {
          cardColorSelects.slice(1).forEach((followingSelect) => {
            followingSelect.value = select.value;
          });
        }
        syncStickyColor(card);
        setMoonPack(card);
      }));
    });

    stickyColor?.addEventListener('change', () => {
      const firstSelect = selectedCard()?.querySelector('[data-color-select]');
      if (!firstSelect) return;
      firstSelect.value = stickyColor.value;
      firstSelect.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const mountMoon = () => {
      const nextMoon = moonHost?.querySelector('moonbundle-component');
      if (!nextMoon) {
        moonComponent = null;
        setSubmitState(false);
        setBundleStatus('L’offre Moon Bundle doit être reliée à ce produit avant l’ajout au panier.');
        return;
      }
      moonComponent = nextMoon;
      if (moonOptions().length < 3) {
        setSubmitState(false);
        setBundleStatus('Chargement de l’offre Moon Bundle…');
        return;
      }
      if (root.dataset.kalluceV2MoonDefaultApplied !== 'true') {
        root.dataset.kalluceV2MoonDefaultApplied = 'true';
        selectPack(packCards[1] || packCards[0]);
      } else {
        setMoonPack(selectedCard());
      }
      window.setTimeout(refreshMoonState, 120);
    };

    const scheduleMoonMount = () => {
      if (moonSyncFrame) return;
      moonSyncFrame = window.requestAnimationFrame(() => {
        moonSyncFrame = 0;
        mountMoon();
      });
    };

    if (moonHost) {
      const containsMoon = (node) => node?.nodeType === 1
        && (node.matches('moonbundle-component') || node.querySelector('moonbundle-component'));
      const moonObserver = new MutationObserver((records) => {
        // Ignore our own CTA/status text and custom-card updates. Observing the
        // entire form without this filter creates a disable/enable feedback loop.
        const changed = records.some((record) => moonComponent?.contains(record.target)
          || [...record.addedNodes, ...record.removedNodes].some(containsMoon));
        if (changed) scheduleMoonMount();
      });
      moonObserver.observe(moonHost, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    if (mainSubmit) {
      const submitObserver = new MutationObserver(refreshMoonState);
      submitObserver.observe(mainSubmit, { attributes: true, attributeFilter: ['class', 'aria-busy'] });
    }

    productForm?.addEventListener('submit', (event) => {
      if (moonIsReadyFor(selectedCard()) && nativeAvailable && !nativeSubmitBusy()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setSubmitState(false);
      if (nativeSubmitBusy()) return;
      setBundleStatus('L’offre Moon Bundle doit être active avant l’ajout au panier.');
      root.querySelector('[data-bundle-shell]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);

    let stickyFrame = 0;
    const updateSticky = () => {
      stickyFrame = 0;
      if (!sticky) return;
      const hero = root.querySelector('.kalluce-v2__hero');
      const visible = hero ? hero.getBoundingClientRect().bottom < window.innerHeight * 0.22 : window.scrollY > 900;
      sticky.hidden = !visible;
    };
    const scheduleSticky = () => {
      if (stickyFrame) return;
      stickyFrame = window.requestAnimationFrame(updateSticky);
    };
    window.addEventListener('scroll', scheduleSticky, { passive: true });
    window.addEventListener('resize', scheduleSticky, { passive: true });
    sticky?.querySelector('[data-sticky-submit]')?.addEventListener('click', () => {
      if (mainSubmit && !mainSubmit.disabled) mainSubmit.click();
      else root.querySelector('[data-bundle-shell]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    root.querySelectorAll('[data-scroll-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-scroll-track]');
      if (!track) return;
      const move = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.82, behavior: 'smooth' });
      carousel.querySelector('[data-scroll-prev]')?.addEventListener('click', () => move(-1));
      carousel.querySelector('[data-scroll-next]')?.addEventListener('click', () => move(1));
    });

    const mini = root.querySelector('[data-mini-carousel]');
    const miniTrack = mini?.querySelector('[data-mini-track]');
    const miniDots = [...(mini?.querySelectorAll('.kalluce-v2__carousel-controls i') || [])];
    let miniIndex = 0;
    const showMini = (requestedIndex) => {
      if (!miniTrack) return;
      miniIndex = (requestedIndex + 3) % 3;
      miniTrack.style.transform = `translateX(-${miniIndex * 100}%)`;
      miniDots.forEach((dot, index) => dot.classList.toggle('is-active', index === miniIndex));
    };
    mini?.querySelector('[data-mini-prev]')?.addEventListener('click', () => showMini(miniIndex - 1));
    mini?.querySelector('[data-mini-next]')?.addEventListener('click', () => showMini(miniIndex + 1));

    showMedia(0);
    selectPack(packCards[1] || packCards[0], false);
    mountMoon();
    scheduleSticky();
  };

  const initAll = () => document.querySelectorAll('[data-kalluce-v2]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
